import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash.clonedeep';
import isNull from 'lodash.isnull';
import isUndefined from 'lodash.isundefined';
import { startOfDay, endOfDay, subDays } from 'date-fns';
import ReactTooltip from 'react-tooltip';
import { Link } from 'react-router-dom';

import { Button, DropdownButton } from '../../../components';
import FilterOptionsModal from './FilterModal';
import ProviderFilterModal from './ProviderFilterModal';
import {
	loadAdtNotifications,
	markAdtRead,
	loadAdtFilterOptions,
	starAdt,
	resetFilter,
	loadExtendedFilterOptions,
	PAGE_SIZES,
	loadPayerFilterOptions,
} from './api';
import { InlineFilter, AdtRow, DateRangePickerDropdown } from './components';

const DEFAULT_FILTER_OPTIONS = {
	record_limit: 10,
	display_unread_notifications: true,
	// deceased_only: false,
	starred_message: false,
	adt_types: null,
	adt_class: null,
	facility_filter: null,
	provider_filter: null,
	discharge_dispositions: null,
	offset: 0,
	primary_payers: null,
	secondary_payers: null,
};

export default class ADTViewer extends React.Component {
	static propTypes = {
		user: PropTypes.object.isRequired,
		showNotification: PropTypes.func,
		onShowCareTeam: PropTypes.func.isRequired,
		onOpenInstavisit: PropTypes.func.isRequired,
		onShowNotes: PropTypes.func.isRequired,
		onShowPatientADTs: PropTypes.func.isRequired,
		selectedAdt: PropTypes.object,
		onShowCCDA: PropTypes.func,
		onShowAdditionalDetails: PropTypes.func,
		hasCampaigns: PropTypes.bool,
		onOpenAddToCampaign: PropTypes.func,
		isCampaignModalOpen: PropTypes.bool,
	};

	state = {
		hasLoadedData: false,
		isLoading: false,
		isError: false,
		data: [],
		filter: cloneDeep(DEFAULT_FILTER_OPTIONS),
		isFilterOptionsModalOpen: false,
		acrsCount: {},
		isPreparingFilterOptions: true,
		isFilterPanelOpen: true,
		isProviderFilterModalOpen: false,
		startDate: subDays(startOfDay(new Date()), 90),
		endDate: endOfDay(new Date()),
	};

	async componentDidMount() {
		document.getElementById('body').classList.add('adt-viewer');
		await this.setupLocalFilter();
	}

	setupLocalFilter = async () => {
		const user = this.props.user.toJSON();
		this.setState({ isLoadingFilterOptions: true });

		let { filter_options, current_saved_filter } = await loadAdtFilterOptions(
			user.id
		);

		if (!current_saved_filter || isNull(current_saved_filter)) {
			current_saved_filter = cloneDeep(DEFAULT_FILTER_OPTIONS);
		}

		if (isUndefined(current_saved_filter.display_unread_notifications)) {
			current_saved_filter.display_unread_notifications = false;
		}
		if (isUndefined(current_saved_filter.starred_message)) {
			current_saved_filter.starred_message = false;
		}
		// console.log(current_saved_filter.adt_class)
		if (Array.isArray(current_saved_filter.adt_class)) {
			const outpatientIndex = current_saved_filter.adt_class.findIndex(
				(clss) => clss.adt_class_type === 'O'
			);
			if (outpatientIndex >= 0) {
				current_saved_filter.adt_class.splice(outpatientIndex, 1);
			}
		}

		// delete current_saved_filter.isPatientSearch;
		// delete current_saved_filter.first_name;
		// delete current_saved_filter.last_name;
		// delete current_saved_filter.dob;

		if (
			Array.isArray(current_saved_filter.primary_payers) &&
			current_saved_filter.primary_payers.length === 0
		) {
			current_saved_filter.primary_payers = null;
		}
		if (
			Array.isArray(current_saved_filter.secondary_payers) &&
			current_saved_filter.secondary_payers.length === 0
		) {
			current_saved_filter.secondary_payers = null;
		}

		// by default, load the last 90 days
		const minDate = subDays(startOfDay(new Date()), 90);
		const maxDate = endOfDay(new Date());

		this.setState(
			() => ({
				minDate,
				maxDate,
				startDate: minDate,
				endDate: maxDate,
				isLoadingFilterOptions: false,
				filterOptions: filter_options,
				filter: {
					...DEFAULT_FILTER_OPTIONS,
					...current_saved_filter,
					start_date: minDate.toISOString(),
					end_date: maxDate.toISOString(),
				},
			}),
			this.loadData
		);
	};

	loadData = async () => {
		const user = this.props.user.toJSON();
		const { filter } = this.state;

		this.setState({
			isLoading: true,
			isError: false,
			data: [],
			isPreparingFilterOptions: true,
		});

		// const { filter_options, current_saved_filter, notification_saved_filter } = await loadAdtFilterOptions(user.id);
		// // console.log(filterOptions)
		// this.setState({
		// 	isLoadingFilterOptions: false,
		// 	filterOptions: filter_options,
		// 	filter: current_saved_filter || DEFAULT_FILTER_OPTIONS
		// });

		try {
			const { acrs_result = [], acrs_count } = await loadAdtNotifications(
				user.id,
				filter
			);
			this.setState({
				hasLoadedData: true,
				isLoading: false,
				data: acrs_result,
				acrsCount: acrs_count[0],
			});
		} catch (e) {
			this.props.showNotification({
				message: 'Unable to load ADT data!',
				status: 'danger',
				timeout: 10000,
			});
			this.setState({ isLoading: false, isError: true });
		}

		Promise.allSettled([
			// loadAdtProviderFilterOptions(user.id),
			loadExtendedFilterOptions(user.id),
			loadPayerFilterOptions(user.id),
		]).then(
			([
				/*{ value: { filter_options: providerOptions } },*/ {
					value: { filter_options: extendedOptions },
				},
				{
					value: { filter_options },
				},
			]) => {
				const { primaryPayers, secondaryPayers } =
					filter_options.extended_filter;
				const primary_payers = {};
				const secondary_payers = {};
				primaryPayers.forEach(({ payer, plan }) => {
					if (Array.isArray(primary_payers[payer])) {
						primary_payers[payer].push(plan);
					} else {
						primary_payers[payer] = [plan];
					}
				});
				secondaryPayers.forEach(({ payer, plan }) => {
					if (Array.isArray(secondary_payers[payer])) {
						secondary_payers[payer].push(plan);
					} else {
						secondary_payers[payer] = [plan];
					}
				});
				this.setState((currState) => ({
					isPreparingFilterOptions: false,
					// providerOptions,
					extendedOptions,
					payerOptions: { primary_payers, secondary_payers },
					filterOptions: {
						...currState.filterOptions,
						// ...providerOptions,
						...extendedOptions.extended_filter,
						primary_payers,
						secondary_payers,
					},
				}));
			}
		);
	};

	componentDidUpdate(prevProps) {
		if (prevProps.isCampaignModalOpen && !this.props.isCampaignModalOpen) {
			// reload data
			this.loadData();
		}
	}

	componentWillUnmount() {
		document.getElementById('body').classList.remove('adt-viewer');
	}

	refreshData = () => {
		const { filter } = this.state;
		// const newOffset = filter.offset + filter.record_limit;
		this.setState(
			() => ({
				filter: {
					...filter,
					offset: 0,
				},
			}),
			this.loadData
		);
	};

	handleResetFilter = () => {
		const { user } = this.props;
		this.setState({ isLoading: true });
		resetFilter(user.get('id')).then(this.setupLocalFilter);
	};

	handleRecordLimitChange = (newPageSize) => {
		const { filter } = this.state;
		delete filter.start_id;
		this.setState(
			() => ({
				filter: {
					...filter,
					record_limit: newPageSize,
					offset: 0,
				},
			}),
			this.loadData
		);
	};

	handleNextClick = () => {
		const { filter } = this.state;
		const newOffset = filter.offset + filter.record_limit;
		this.setState(
			() => ({
				filter: {
					...filter,
					offset: newOffset,
				},
			}),
			this.loadData
		);
	};

	handlePreviousClick = () => {
		const { filter } = this.state;
		let newOffset = filter.offset - filter.record_limit;
		if (newOffset < 0) {
			newOffset = 0;
		}
		this.setState(
			() => ({
				filter: {
					...filter,
					offset: newOffset,
				},
			}),
			this.loadData
		);
	};

	handleToggleReadIndicator = async (notificationId, markRead) => {
		const userId = this.props.user.get('id');
		this.setState({ isLoading: true });
		try {
			await markAdtRead(userId, notificationId, markRead);
			this.loadData();
		} catch (e) {
			// console.log(e)
			this.setState({ isLoading: false });
		}
	};

	handleStarIndicator = async (notificationId, isStarred) => {
		const userId = this.props.user.get('id');
		try {
			const { data } = await starAdt(userId, notificationId, !isStarred);
			if (data.cb && data.cb.updated) {
				const notificationIndex = this.state.data.findIndex(
					({ id }) => id === notificationId
				);
				if (notificationIndex >= 0) {
					const newData = [...this.state.data];
					newData[notificationIndex].starred_ind = !isStarred;
					this.setState({ data: newData });
				}
			}
		} catch (e) {
			// console.log(e)
		}
	};

	handleCloseFilterOptionsModal = () => {
		this.setState({ isFilterOptionsModalOpen: false });
	};

	/**
	 * This can be called from InlineFilter, ProviderFilterModal, and FilterOptionsModal
	 *
	 * @param {*} filterSelections
	 */
	handleFiltersChosen = (filterSelections) => {
		const { filterOptions, filter } = this.state;
		// combine current filter with incoming selections
		const localSelections = cloneDeep({ ...filter, ...filterSelections });

		Object.keys(localSelections).forEach((key) => {
			if (
				Array.isArray(localSelections[key]) &&
				Array.isArray(filterOptions[key]) &&
				localSelections[key].length === filterOptions[key].length
			) {
				if (!['employers', 'locations', 'programs'].includes(key)) {
					// handle "all options" selected - convert to null
					localSelections[key] = null;
				}
			}
		});

		// handle "all options" selection - remove completely
		// ['employers', 'locations', 'programs'].forEach((nullable) => {
		// 	if (Array.isArray(localSelections[nullable]) && Array.isArray(filterOptions[nullable]) && (localSelections[nullable].length === filterOptions[nullable].length)) {
		// 		delete localSelections[nullable];
		// 	}
		// });

		this.setState(
			() => ({
				isFilterOptionsModalOpen: false,
				isProviderFilterModalOpen: false,
				filter: {
					...localSelections,
					offset: 0,
				},
			}),
			this.loadData
		);
	};

	toggleFilterPanel = () => {
		this.setState((currState) => ({
			isFilterPanelOpen: !currState.isFilterPanelOpen,
		}));
	};

	handleDateRangeChosen = ({ startDate, endDate }) => {
		const { filter } = this.state;
		this.setState(
			() => ({
				startDate,
				endDate,
				filter: {
					...filter,
					start_date: startDate.toISOString(),
					end_date: endDate.toISOString(),
				},
			}),
			this.loadData
		);
	};

	render() {
		const {
			data,
			filter,
			isLoading,
			acrsCount,
			isPreparingFilterOptions,
			isFilterPanelOpen,
			minDate,
			maxDate,
			startDate,
			endDate,
			isLoadingFilterOptions,
			isFilterOptionsModalOpen,
			filterOptions,
			isError,
			hasLoadedData,
			isProviderFilterModalOpen,
			filterBy,
		} = this.state;
		const {
			selectedAdt,
			user,
			onShowCCDA,
			onShowCareTeam,
			onShowNotes,
			onOpenInstavisit,
			onShowPatientADTs,
			onShowAdditionalDetails,
			hasCampaigns,
			onOpenAddToCampaign,
		} = this.props;

		const startingAt = filter.offset + 1;
		// console.log(filterOptions)
		const disableADTFeatures =
			!!this.props.user.get('provider_practice').disable_adt_features_ind;

		return (
			<div className="content-wrapper adt-viewer">
				<ol className="breadcrumb">
					<li>
						<Link to="/">Home</Link>
					</li>
					<li>ADT</li>
					<li className="active">Viewer</li>
				</ol>
				<FilterOptionsModal
					isOpen={isFilterOptionsModalOpen}
					onClose={this.handleCloseFilterOptionsModal}
					onSave={this.handleFiltersChosen}
					filterOptions={filterOptions}
					currentFilter={filter}
					filterBy={filterBy}
				/>
				<ProviderFilterModal
					isOpen={isProviderFilterModalOpen}
					onClose={() => this.setState({ isProviderFilterModalOpen: false })}
					onSave={this.handleFiltersChosen}
					// filterOptions={filterOptions}
					currentFilter={filter}
					providerId={this.props.user.get('id')}
				/>
				<div className="table-grid table-grid-desktop">
					{isFilterPanelOpen && (
						<div className="col col-md">
							<div className="pr">
								<div className="mb-boxes collapse">
									<InlineFilter
										isLoading={isPreparingFilterOptions}
										filterOptions={filterOptions}
										currentFilter={filter}
										onApply={this.handleFiltersChosen}
										onShowMoreFilters={(filterBy) =>
											this.setState({
												isFilterOptionsModalOpen: true,
												filterBy,
											})
										}
										onShowProviderFilter={() =>
											this.setState({ isProviderFilterModalOpen: true })
										}
										onReset={this.handleResetFilter}
									/>
								</div>
							</div>
						</div>
					)}
					<div className="col">
						<div className="panel panel-default">
							<div className="panel-heading adt-header">
								<div className="row">
									<div className="col-md-4">
										<Button
											disabled={isLoadingFilterOptions || isLoading}
											icon="filter"
											altTitle={
												isFilterPanelOpen
													? 'Hide Filter Options'
													: 'Show Filter Options'
											}
											onClick={this.toggleFilterPanel}
											className="mr"
											icon2={isFilterPanelOpen ? 'caret-left' : 'caret-right'}
											primary
										/>
										<span className="h4">
											Showing{' '}
											{isLoading ? (
												<em className="fa fa-spin fa-circle-o-notch" />
											) : (
												`${startingAt} - ${filter.offset + data.length} of ${
													acrsCount.result_count || 0
												}`
											)}
										</span>
										<Button
											disabled={isLoadingFilterOptions || isLoading}
											icon="refresh"
											altTitle="Refresh"
											onClick={this.refreshData}
											className="btn-default ml"
										/>
									</div>
									<div className="col-md-4 text-center">
										<div className="btn-group" role="group" aria-label="...">
											<Button
												icon="arrow-left"
												onClick={this.handlePreviousClick}
												disabled={filter.offset === 0}
												altTitle="Previous"
											/>
											<div className="btn-group" role="group">
												<DropdownButton
													title={`Showing ${
														filter.record_limit || '10'
													} records`}
													items={PAGE_SIZES.map((pageSize) => ({
														title: `${pageSize} per page`,
														onClick: () =>
															this.handleRecordLimitChange(pageSize),
													}))}
												/>
											</div>
											<Button
												icon="arrow-right"
												onClick={this.handleNextClick}
												disabled={data.length < filter.record_limit}
												altTitle="Next"
											/>
										</div>
									</div>
									<div className="col-md-4 text-right">
										<a data-tip data-for="max-range-info">
											<em className="fa fa-info-circle" />
										</a>
										<ReactTooltip
											place="left"
											type="info"
											effect="solid"
											id="max-range-info"
										>
											<span>
												CareConvene only stores up to 90 days of ADT records
											</span>
										</ReactTooltip>
										<DateRangePickerDropdown
											maxDate={maxDate}
											minDate={minDate}
											startDate={startDate}
											endDate={endDate}
											onRangeChosen={this.handleDateRangeChosen}
										/>
									</div>
								</div>
							</div>
							<div className="panel-body adt-list">
								{isError && data.length === 0 && (
									<div className="row">
										<div className="col-md-12 mt">
											Unable to load ADT data. Check your selected filters and
											try again.
										</div>
									</div>
								)}
								{hasLoadedData && !isLoading && data.length === 0 && (
									<div className="row">
										<div className="col-md-12 mt">
											No data found. Check your selected filters and try again.
										</div>
									</div>
								)}
								{isLoading && data.length === 0 && (
									<div className="row">
										<div className="col-md-12 mt">Loading data...</div>
									</div>
								)}
								{data.map((adt) => (
									<AdtRow
										key={adt.id}
										adt={adt}
										isLoading={isLoading}
										userId={user.get('id')}
										selectedAdt={selectedAdt}
										onShowNotes={onShowNotes}
										onShowCareTeam={onShowCareTeam}
										onOpenInstavisit={onOpenInstavisit}
										onShowPatientADTs={onShowPatientADTs}
										onShowCCDA={onShowCCDA}
										onToggleReadIndicator={this.handleToggleReadIndicator}
										onToggleStarIndicator={this.handleStarIndicator}
										disableADTFeatures={disableADTFeatures}
										onShowAdditionalDetails={onShowAdditionalDetails}
										hasCampaigns={hasCampaigns}
										onOpenAddToCampaign={onOpenAddToCampaign}
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
}
